import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { app } from '../firebase';

const ResetPassword: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validatingCode, setValidatingCode] = useState(true);
    const [email, setEmail] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth(app);

    // Get oobCode from URL parameters
    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode');

    useEffect(() => {
        const validateCode = async () => {
            if (!oobCode) {
                setError('Invalid password reset link');
                setValidatingCode(false);
                return;
            }

            try {
                // Verify the password reset code and get the email
                const email = await verifyPasswordResetCode(auth, oobCode);
                setEmail(email);
                setValidatingCode(false);
            } catch (err: any) {
                setError('This password reset link is invalid or has expired');
                setValidatingCode(false);
            }
        };

        validateCode();
    }, [auth, oobCode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!oobCode) {
            setError('Invalid password reset link');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password should be at least 6 characters');
            return;
        }

        setError('');
        setLoading(true);

        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setSuccess(true);
            setTimeout(() => {
                navigate('/signin');
            }, 3000);
        } catch (err: any) {
            setError(err.message);
        }
        setLoading(false);
    };

    if (validatingCode) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center">
                <div className="text-white">Verifying reset link...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Header */}
            <header className="bg-gray-800 py-4">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center gap-3">
                    <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                    <Link to="/" className="text-xl sm:text-2xl font-bold text-cyan-400">Fixisy</Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
                <div className="w-full max-w-xs sm:max-w-md">
                    <div className="mb-6 sm:mb-8 text-center">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Reset Password</h1>
                        {email && (
                            <p className="text-gray-400 text-sm sm:text-base">
                                for {email}
                            </p>
                        )}
                    </div>

                    {success ? (
                        <div className="text-center">
                            <div className="mb-4 text-green-400 text-sm sm:text-base">
                                Your password has been successfully reset.
                                Redirecting to sign in page...
                            </div>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-4 text-red-400 text-center text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                                <div>
                                    <input
                                        type="password"
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                                        required
                                    />
                                </div>
                                <div>
                                    <input
                                        type="password"
                                        placeholder="Confirm New Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-cyan-500 text-white py-3 rounded-lg font-semibold hover:bg-cyan-600 transition-colors text-sm sm:text-base disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading ? 'Resetting Password...' : 'Reset Password'}
                                </button>
                            </form>

                            <div className="mt-4 text-center">
                                <Link
                                    to="/signin"
                                    className="text-cyan-400 hover:text-cyan-300 text-sm sm:text-base"
                                >
                                    Back to Sign In
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="py-6 sm:py-8 bg-gray-800 mt-auto">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
                        <div className="text-gray-400 mb-2 sm:mb-0 text-center sm:text-left text-xs sm:text-base">
                            © {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 items-center">
                            <Link to="/terms" className="text-gray-400 hover:text-cyan-400">
                                Terms & Conditions
                            </Link>
                            <Link to="/privacy" className="text-gray-400 hover:text-cyan-400">
                                Privacy Policy
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ResetPassword; 